import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { trackSignUp, trackLogin } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Loader2, Mail, KeyRound } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login | register | magic_link | magic_verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [magicCode, setMagicCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageSEO({ title: "Sign In", description: "Sign in or create your Stansbury AI account to access your AI Growth Blueprint.", path: "/auth" });

  // Auto-fill from magic link URL or mode param
  useEffect(() => {
    const urlMagic = searchParams.get("magic");
    const urlEmail = searchParams.get("email");
    const urlMode = searchParams.get("mode");
    if (urlMagic && urlEmail) {
      setEmail(urlEmail);
      setMagicCode(urlMagic);
      setMode("magic_verify");
    } else if (urlMode === "register") {
      setMode("register");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await login(email, password);
        trackLogin("email");
        navigate(data.role === "admin" ? "/ops" : "/dashboard");
      } else if (mode === "register") {
        await register(email, password, name, companyName);
        trackSignUp("email");
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link", { email });
      setSuccess(`Verification code sent! Your code: ${data.code}`);
      setMagicCode(data.code);
      setMode("magic_verify");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link/verify", { email, token: magicCode });
      if (data.token) sessionStorage.setItem("auth_token", data.token);
      await checkAuth();
      navigate(data.role === "admin" ? "/ops" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="auth-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] text-white flex-col justify-between p-12">
        <div>
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="auth-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-['IBM_Plex_Sans']">Back to home</span>
          </button>
        </div>
        <div>
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-slate-500 mb-4"><img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-14 mb-2" /></p>
          <h1 className="text-4xl font-['Outfit'] font-medium tracking-tight mb-6">
            Discover where AI<br />will grow your business.
          </h1>
          <p className="text-lg text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed max-w-md">
            Get a structured blueprint that reveals revenue leaks, automation wins, and the exact systems to build next.
          </p>
        </div>
        <p className="text-xs text-slate-600 font-['IBM_Plex_Sans']"><a href="/privacy" className="hover:underline">Privacy Policy</a> · Confidential & Secure</p>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="lg:hidden flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-8">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Back</span>
          </button>

          <h2 className="text-2xl font-['Outfit'] font-medium tracking-tight mb-2">
            {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : mode === "magic_link" ? "Sign in with magic link" : "Enter verification code"}
          </h2>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-8">
            {mode === "login" ? "Sign in to access your blueprint dashboard." : mode === "register" ? "Start your Stansbury AI audit today." : mode === "magic_link" ? "We'll send a one-time code to your email." : "Enter the 6-character code we sent you."}
          </p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6" data-testid="auth-error">{error}</div>}
          {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-3 mb-6" data-testid="auth-success">{success}</div>}

          {/* Password Login/Register */}
          {(mode === "login" || mode === "register") && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === "register" && (
                <>
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
                    <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="mt-1.5 rounded-none border-slate-300 focus:ring-[#0033CC]/20 focus:border-[#0033CC]" data-testid="auth-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-sm font-medium text-slate-700">Company Name</Label>
                    <Input id="company" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Inc." className="mt-1.5 rounded-none border-slate-300 focus:ring-[#0033CC]/20 focus:border-[#0033CC]" data-testid="auth-company-input" />
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="mt-1.5 rounded-none border-slate-300 focus:ring-[#0033CC]/20 focus:border-[#0033CC]" data-testid="auth-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Min. 6 characters" className="mt-1.5 rounded-none border-slate-300 focus:ring-[#0033CC]/20 focus:border-[#0033CC]" data-testid="auth-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium" data-testid="auth-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>
          )}

          {/* Magic Link Request */}
          {mode === "magic_link" && (
            <form onSubmit={handleMagicLinkRequest} className="space-y-5">
              <div>
                <Label htmlFor="magic-email" className="text-sm font-medium text-slate-700">Email</Label>
                <Input id="magic-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="mt-1.5 rounded-none border-slate-300 focus:ring-[#0033CC]/20 focus:border-[#0033CC]" data-testid="magic-email-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium" data-testid="magic-link-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Send Magic Code</>}
              </Button>
            </form>
          )}

          {/* Magic Link Verify */}
          {mode === "magic_verify" && (
            <form onSubmit={handleMagicVerify} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-none border-slate-300 bg-slate-50" data-testid="verify-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Verification Code</Label>
                <Input type="text" value={magicCode} onChange={(e) => setMagicCode(e.target.value.toUpperCase())} required maxLength={6} placeholder="6-character code" className="mt-1.5 rounded-none border-slate-300 font-['JetBrains_Mono'] text-center text-lg tracking-[0.3em] uppercase" data-testid="magic-code-input" />
              </div>
              <Button type="submit" disabled={loading || magicCode.length < 6} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium" data-testid="verify-code-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" /> Verify & Sign In</>}
              </Button>
              <button type="button" onClick={() => switchMode("magic_link")} className="text-sm text-[#0033CC] hover:underline block mx-auto" data-testid="resend-code-btn">
                Resend code
              </button>
            </form>
          )}

          {/* Mode Switching */}
          <div className="mt-6 space-y-3 text-center">
            {mode === "login" && (
              <button onClick={() => switchMode("magic_link")} className="text-sm text-[#0033CC] hover:underline flex items-center justify-center gap-2 mx-auto font-['IBM_Plex_Sans']" data-testid="switch-magic-link">
                <Mail className="w-3.5 h-3.5" /> Use magic link instead
              </button>
            )}
            {(mode === "login" || mode === "register") && (
              <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">
                  {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                  <button onClick={() => switchMode(mode === "login" ? "register" : "login")} className="text-[#0033CC] font-medium hover:underline" data-testid="auth-toggle-mode">
                    {mode === "login" ? "Create one" : "Sign in"}
                  </button>
                </p>
            )}
            {(mode === "magic_link" || mode === "magic_verify") && (
              <button onClick={() => switchMode("login")} className="text-sm text-[#0033CC] hover:underline flex items-center justify-center gap-2 mx-auto font-['IBM_Plex_Sans']" data-testid="switch-password-login">
                <KeyRound className="w-3.5 h-3.5" /> Use password instead
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
